import { AttachmentsBoxVMConfig } from 'components/attachments/box/box';
import { WCCAttachmentLayoutVMTheme } from 'components/attachments/layouts/base/base.interfaces';
import { wccModules } from 'enums/wccModules';
import { AttachmentsHelpers } from 'helpers/attachments';
import { inject, injectable } from 'inversify';
import { Observable, Subscribable, SubscribableOrNullableValue } from 'knockout';
import { IWCCStorageManager } from 'managers/iStorage';
import TopicAttachmentsManager, { AttachmentsView } from 'managers/topic/attachments';
import WCCFile from 'models/attachments/file';
import WCCImage from 'models/attachments/image';
import WCCLink from 'models/attachments/link';
import WCCVideo from 'models/attachments/video';
import WCCVimeoVideo from 'models/attachments/vimeo';
import WCCYoutubeVideo from 'models/attachments/youtube';
import './attachments.html';
import './attachments.scss';

components.preload('sliding-container', 'attachments-box');

type TopicAttachmentsVMTheme = 'light' | 'overlay';

export interface TopicAttachmentsVMConfig {
    topicId?: SubscribableOrNullableValue<string>
    itemWidth?: SubscribableOrNullableValue<number>
    offset?: SubscribableOrNullableValue<number>
    outerOffset?: SubscribableOrNullableValue<number>
    alwaysActive?: SubscribableOrNullableValue<boolean>
    theme?: SubscribableOrNullableValue<TopicAttachmentsVMTheme>
    attachmentsTheme?: SubscribableOrNullableValue<WCCAttachmentLayoutVMTheme>
}

@injectable()
export default class TopicAttachmentsVM {
    private theme: Subscribable<TopicAttachmentsVMTheme>

    attachmentsToggleLabel: Subscribable<string>

    boxConfig: AttachmentsBoxVMConfig

    isBodyShown: Subscribable<boolean>
    areAttachmentsShown: Observable<boolean>
    isAttachmentsToggleShown: Subscribable<boolean>
    isReady: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) config: TopicAttachmentsVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager
    ) {
        const topicId = ko.flattenComputed(config.topicId);
        const isAlwaysActive = ko.flattenComputed(config.alwaysActive, false);
        this.theme = ko.flattenComputed(config.theme, 'light');

        const attachmentsManager = storage.get(TopicAttachmentsManager, { topicId, view: AttachmentsView.all });
        const attachments = attachmentsManager.pluck(m => m.attachments, []);
        const isLoadingAttachments = attachmentsManager.pluck(m => m.loading, true).extend({ deferred: true });

        this.boxConfig = {
            attachments: attachments,
            itemWidth: config.itemWidth,
            offset: config.offset,
            outerOffset: config.outerOffset,
            theme: config.attachmentsTheme
        }

        const images = attachments.filter<WCCImage>(a => a.fileType() === enums.AttachmentTypes.Image.value);
        const videos = attachments.filter<WCCYoutubeVideo | WCCVimeoVideo | WCCVideo>(a => AttachmentsHelpers.isYouTube(a.fileType()) || AttachmentsHelpers.isVimeo(a.fileType()) || AttachmentsHelpers.isVideo(a.fileType()));
        const files = attachments.filter<WCCFile>(a => a.isFile());
        const links = attachments.filter<WCCLink>(a => a.isLink());
        const hasAttachments = ko.pureComputed(() => images().length > 0 || videos().length > 0 || files().length > 0 || links().length > 0);

        this.areAttachmentsShown = ko.observable<boolean>(true);
        this.isAttachmentsToggleShown = ko.observable<boolean>(false);//isAlwaysActive.not();  // switched toggle off.
        this.attachmentsToggleLabel = ko.pureComputed(() => this.areAttachmentsShown() ? labels.HideResourcesList : labels.ViewResourcesList);

        const isActive = ko.pureComputed(() => isAlwaysActive() || this.areAttachmentsShown());

        this.isReady = ko.pureComputed(() => !isLoadingAttachments());

        this.isBodyShown = ko.pureComputed(() => this.isReady() && hasAttachments());

        ko.applyBindingsToNode(info.element, {
            'css': {
                'topic__attachments': true,
                'topic__attachments--active': isActive,
                'topic__attachments--light': this.theme.is('light'),
                'topic__attachments--overlay': this.theme.is('overlay')
            }
        });
    }

    toggle() {
        this.areAttachmentsShown.toggle();
    }
}